/* Quick-Actions modals wired into the DealerShell "+ New ▾" menu.
   All 7 modals talk to existing backend endpoints — no new routes, no mocks. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/* ---------- Reusable shell ---------- */
function Modal({ open, onClose, title, helper, children, footer, testid, wide }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 bg-black/60 backdrop-blur-sm overflow-y-auto"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid={testid}>
      <div className={`bg-[#0b1e36] text-white border border-white/10 rounded-xl shadow-2xl w-full ${wide ? "max-w-3xl" : "max-w-xl"} my-8`}>
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 font-bold">Quick Action</div>
            <h2 className="text-lg font-extrabold mt-0.5" data-testid="qa-modal-title">{title}</h2>
            {helper && <p className="text-xs text-white/60 mt-1">{helper}</p>}
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xl leading-none px-2" data-testid="qa-modal-close" aria-label="Close">×</button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2 bg-white/5 rounded-b-xl">{footer}</div>}
      </div>
    </div>
  );
}

function Field({ label, required, children, hint, error }) {
  return (
    <label className="block mb-3">
      <div className="text-[11px] uppercase tracking-wider text-white/60 font-semibold mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </div>
      {children}
      {hint && !error && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
      {error && <div className="text-[11px] text-red-300 mt-1" data-testid="qa-field-error">{error}</div>}
    </label>
  );
}

const inputCls = "w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-blue-400";

function SaveBtn({ saving, label = "Save", testid }) {
  return (
    <button type="submit" disabled={saving}
      className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded"
      data-testid={testid || "qa-save"}>
      {saving ? "Saving…" : label}
    </button>
  );
}

function CancelBtn({ onClick, testid }) {
  return (
    <button type="button" onClick={onClick}
      className="bg-transparent hover:bg-white/10 text-white/70 text-sm px-3 py-2 rounded"
      data-testid={testid || "qa-cancel"}>Cancel</button>
  );
}

function Banner({ type, children, testid }) {
  if (!children) return null;
  const cls = type === "error" ? "bg-red-900/40 border-red-400/40 text-red-200"
            : type === "success" ? "bg-emerald-900/40 border-emerald-400/40 text-emerald-200"
            : "bg-amber-900/40 border-amber-400/40 text-amber-200";
  return <div className={`text-xs border rounded px-3 py-2 mb-3 ${cls}`} data-testid={testid}>{children}</div>;
}

/* ---------- Customer autocomplete ---------- */
function CustomerPicker({ value, onChange, testid = "qa-customer-picker" }) {
  const [q, setQ] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const tRef = useRef(null);

  useEffect(() => { setQ(value?.name || ""); }, [value]);

  const search = useCallback(async (term) => {
    if (!term || term.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/crm/search`, { params: { q: term, limit: 8 } });
      setResults(data.items || data.results || []);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  const onType = (e) => {
    const v = e.target.value;
    setQ(v);
    setOpen(true);
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => search(v), 200);
  };

  return (
    <div className="relative">
      <input value={q} onChange={onType} onFocus={() => setOpen(true)}
        placeholder="Search by name, phone, email…"
        className={inputCls} data-testid={testid} />
      {open && (q.length >= 2) && (
        <div className="absolute left-0 right-0 mt-1 bg-[#061426] border border-white/15 rounded shadow-lg max-h-60 overflow-y-auto z-10" data-testid="qa-customer-results">
          {loading && <div className="px-3 py-2 text-xs text-white/50">Searching…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-2 text-xs text-white/50">No customers match — leave blank or create a new customer first.</div>}
          {results.map(r => (
            <button type="button" key={r.id}
              onClick={() => { onChange({ id: r.id, name: r.name, phone: r.phone, email: r.email }); setQ(r.name || ""); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-600/20 border-b border-white/5 last:border-0"
              data-testid={`qa-customer-result-${r.id}`}>
              <div className="font-semibold text-white">{r.name}</div>
              <div className="text-[11px] text-white/60">{[r.phone, r.email].filter(Boolean).join(" · ")}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- New Customer ---------- */
function NewCustomerForm({ onClose, onCreated }) {
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", preferred_contact: "Text", lead_source: "Walk-In / Phone", status: "working", address: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dupe, setDupe] = useState(null);
  const navigate = useNavigate();

  // Check for duplicates by phone or email
  useEffect(() => {
    const term = (f.phone || "").trim() || (f.email || "").trim();
    if (term.length < 5) { setDupe(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/crm/search`, { params: { q: term, limit: 1 } });
        const items = data.items || data.results || [];
        if (items[0]) setDupe(items[0]); else setDupe(null);
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [f.phone, f.email]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.first_name.trim() && !f.last_name.trim()) { setError("First or last name is required."); return; }
    setSaving(true);
    try {
      const payload = {
        name: `${f.first_name} ${f.last_name}`.trim(),
        phone: f.phone || null,
        email: f.email || null,
        address: f.address || null,
        lead_source: f.lead_source,
        preferred_contact: f.preferred_contact,
        message: f.notes || null,
        tags: ["manual"],
      };
      const { data } = await api.post("/dealer/leads/quick-add", payload);
      const cid = data?.lead?.id;
      onCreated?.({ id: cid, name: payload.name });
      onClose();
      if (cid) navigate(`/admin/crm/customer/${cid}`);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to create customer.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="qa-customer-form">
      <Banner type="error" testid="qa-customer-error">{error}</Banner>
      {dupe && <Banner type="warn" testid="qa-customer-dupe">Possible duplicate: <Link to={`/admin/crm/customer/${dupe.id}`} onClick={onClose} className="underline">{dupe.name}</Link> already exists with that phone/email.</Banner>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name" required><input className={inputCls} value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="qa-customer-first" required /></Field>
        <Field label="Last name"><input className={inputCls} value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="qa-customer-last" /></Field>
        <Field label="Phone"><input className={inputCls} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(614) 555-0123" data-testid="qa-customer-phone" /></Field>
        <Field label="Email"><input type="email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@example.com" data-testid="qa-customer-email" /></Field>
        <Field label="Preferred contact">
          <select className={inputCls} value={f.preferred_contact} onChange={(e) => setF({ ...f, preferred_contact: e.target.value })} data-testid="qa-customer-prefcontact">
            <option>Text</option><option>Call</option><option>Email</option>
          </select>
        </Field>
        <Field label="Lead source">
          <select className={inputCls} value={f.lead_source} onChange={(e) => setF({ ...f, lead_source: e.target.value })} data-testid="qa-customer-source">
            <option>Walk-In / Phone</option><option>Website</option><option>Referral</option><option>Repeat Customer</option><option>Other</option>
          </select>
        </Field>
      </div>
      <Field label="Address"><input className={inputCls} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="qa-customer-address" /></Field>
      <Field label="Notes"><textarea className={inputCls} rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="qa-customer-notes" /></Field>
      <div className="flex items-center justify-end gap-2 pt-2">
        <CancelBtn onClick={onClose} testid="qa-customer-cancel" />
        <SaveBtn saving={saving} label="Create Customer" testid="qa-customer-save" />
      </div>
    </form>
  );
}

/* ---------- New Deal (links to a customer; opens that customer's deal jacket) ---------- */
function NewDealForm({ onClose }) {
  const [customer, setCustomer] = useState(null);
  const [f, setF] = useState({ deal_type: "Finance", vehicle_title: "", down_payment: "", est_payment: "", trade_involved: "no", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!customer?.id) { setError("Select an existing customer first."); return; }
    setSaving(true);
    try {
      // Patch the lead with deal info
      await api.patch(`/admin/crm/customer/${customer.id}`, {
        purchase_vehicle: { vehicle_title: f.vehicle_title || null, down_payment: f.down_payment || null, est_payment: f.est_payment || null, deal_type: f.deal_type },
      });
      // Add tags via bulk endpoint (uses $addToSet so it's safe)
      await api.post(`/dealer/leads/bulk`, { lead_ids: [customer.id], action: "add_tag", value: "deal" });
      if (f.trade_involved === "yes") {
        await api.post(`/dealer/leads/bulk`, { lead_ids: [customer.id], action: "add_tag", value: "has_trade" });
      }
      if (f.notes?.trim()) {
        await api.post(`/admin/crm/notes`, { customer_id: customer.id, text: `[Deal] ${f.notes}` });
      }
      onClose();
      navigate(`/admin/crm/customer/${customer.id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to create deal.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="qa-deal-form">
      <Banner type="error" testid="qa-deal-error">{error}</Banner>
      <Field label="Customer" required hint="Pick an existing customer or create one first.">
        <CustomerPicker value={customer} onChange={setCustomer} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Deal type">
          <select className={inputCls} value={f.deal_type} onChange={(e) => setF({ ...f, deal_type: e.target.value })} data-testid="qa-deal-type">
            <option>New</option><option>Used</option><option>Lease</option><option>Finance</option><option>Cash</option>
          </select>
        </Field>
        <Field label="Trade involved?">
          <select className={inputCls} value={f.trade_involved} onChange={(e) => setF({ ...f, trade_involved: e.target.value })} data-testid="qa-deal-trade">
            <option value="no">No</option><option value="yes">Yes</option>
          </select>
        </Field>
      </div>
      <Field label="Vehicle (year/make/model)" hint="Free text — Stock # or VIN can be added on the customer profile."><input className={inputCls} value={f.vehicle_title} onChange={(e) => setF({ ...f, vehicle_title: e.target.value })} placeholder="2025 Chevy Trax LT" data-testid="qa-deal-vehicle" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Down payment"><input className={inputCls} value={f.down_payment} onChange={(e) => setF({ ...f, down_payment: e.target.value })} placeholder="$2,500" data-testid="qa-deal-down" /></Field>
        <Field label="Est. payment"><input className={inputCls} value={f.est_payment} onChange={(e) => setF({ ...f, est_payment: e.target.value })} placeholder="$385/mo" data-testid="qa-deal-payment" /></Field>
      </div>
      <Field label="Notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="qa-deal-notes" /></Field>
      <div className="flex items-center justify-end gap-2 pt-2">
        <CancelBtn onClick={onClose} />
        <SaveBtn saving={saving} label="Create Deal" testid="qa-deal-save" />
      </div>
    </form>
  );
}

/* ---------- New Trade Appraisal ---------- */
function NewTradeForm({ onClose }) {
  const [customer, setCustomer] = useState(null);
  const [createNew, setCreateNew] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", email: "" });
  const [f, setF] = useState({ year: "", make: "", model: "", trim: "", vin: "", mileage: "", condition: "Good", payoff_amount: "", lienholder: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    let cid = customer?.id;
    if (!cid && !createNew) { setError("Select a customer or create one."); return; }
    setSaving(true);
    try {
      if (!cid) {
        if (!newCust.name.trim()) throw new Error("Customer name required");
        const { data } = await api.post("/dealer/leads/quick-add", {
          name: newCust.name.trim(), phone: newCust.phone || null, email: newCust.email || null,
          lead_source: "Trade Appraisal", tags: ["has_trade"],
        });
        cid = data?.lead?.id;
      }
      await api.patch(`/admin/crm/customer/${cid}`, {
        trade_vehicle: {
          year: f.year || null, make: f.make || null, model: f.model || null, trim: f.trim || null,
          vin: f.vin || null, mileage: f.mileage || null, condition: f.condition,
          payoff_amount: f.payoff_amount || null, lienholder: f.lienholder || null, notes: f.notes || null,
        },
      });
      // Make sure has_trade tag is set (quick-add already sets it; bulk add is idempotent via $addToSet)
      await api.post(`/dealer/leads/bulk`, { lead_ids: [cid], action: "add_tag", value: "has_trade" });
      onClose();
      navigate(`/admin/crm/customer/${cid}`);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to create trade appraisal.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="qa-trade-form">
      <Banner type="error" testid="qa-trade-error">{error}</Banner>
      <div className="flex gap-2 text-xs mb-3">
        <button type="button" onClick={() => setCreateNew(false)} className={`px-3 py-1.5 rounded ${!createNew ? "bg-blue-600 text-white" : "bg-white/10 text-white/70"}`} data-testid="qa-trade-pick-existing">Existing Customer</button>
        <button type="button" onClick={() => setCreateNew(true)} className={`px-3 py-1.5 rounded ${createNew ? "bg-blue-600 text-white" : "bg-white/10 text-white/70"}`} data-testid="qa-trade-pick-new">New Customer</button>
      </div>
      {!createNew ? (
        <Field label="Customer" required><CustomerPicker value={customer} onChange={setCustomer} /></Field>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Customer name" required><input className={inputCls} value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} data-testid="qa-trade-newname" required /></Field>
          <Field label="Phone"><input className={inputCls} value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} data-testid="qa-trade-newphone" /></Field>
          <Field label="Email"><input className={inputCls} value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} data-testid="qa-trade-newemail" /></Field>
        </div>
      )}
      <div className="grid grid-cols-4 gap-3">
        <Field label="Year"><input className={inputCls} value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} data-testid="qa-trade-year" /></Field>
        <Field label="Make"><input className={inputCls} value={f.make} onChange={(e) => setF({ ...f, make: e.target.value })} data-testid="qa-trade-make" /></Field>
        <Field label="Model"><input className={inputCls} value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} data-testid="qa-trade-model" /></Field>
        <Field label="Trim"><input className={inputCls} value={f.trim} onChange={(e) => setF({ ...f, trim: e.target.value })} data-testid="qa-trade-trim" /></Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="VIN"><input className={inputCls} value={f.vin} onChange={(e) => setF({ ...f, vin: e.target.value })} data-testid="qa-trade-vin" /></Field>
        <Field label="Mileage"><input className={inputCls} value={f.mileage} onChange={(e) => setF({ ...f, mileage: e.target.value })} data-testid="qa-trade-mileage" /></Field>
        <Field label="Condition">
          <select className={inputCls} value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} data-testid="qa-trade-condition">
            <option>Excellent</option><option>Good</option><option>Fair</option><option>Rough</option>
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Payoff amount"><input className={inputCls} value={f.payoff_amount} onChange={(e) => setF({ ...f, payoff_amount: e.target.value })} data-testid="qa-trade-payoff" /></Field>
        <Field label="Lienholder"><input className={inputCls} value={f.lienholder} onChange={(e) => setF({ ...f, lienholder: e.target.value })} data-testid="qa-trade-lien" /></Field>
      </div>
      <Field label="Vehicle notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="qa-trade-notes" /></Field>
      <div className="flex items-center justify-end gap-2 pt-2">
        <CancelBtn onClick={onClose} />
        <SaveBtn saving={saving} label="Save Appraisal" testid="qa-trade-save" />
      </div>
    </form>
  );
}

/* ---------- New Task ---------- */
function NewTaskForm({ onClose }) {
  const [customer, setCustomer] = useState(null);
  const [f, setF] = useState({ title: "", task_type: "Call", priority: "Normal", due_date: "", due_time: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!customer?.id) { setError("Pick a customer to attach this task to."); return; }
    if (!f.title.trim()) { setError("Task title is required."); return; }
    setSaving(true);
    try {
      let due_at = null;
      if (f.due_date) due_at = `${f.due_date}T${f.due_time || "09:00"}:00`;
      await api.post(`/admin/crm/tasks`, {
        customer_id: customer.id, title: f.title, task_type: f.task_type,
        priority: f.priority, due_at, notes: f.notes || null,
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to create task.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="qa-task-form">
      <Banner type="error" testid="qa-task-error">{error}</Banner>
      <Field label="Customer" required><CustomerPicker value={customer} onChange={setCustomer} /></Field>
      <Field label="Task title" required><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Follow up on test drive" data-testid="qa-task-title" required /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Task type">
          <select className={inputCls} value={f.task_type} onChange={(e) => setF({ ...f, task_type: e.target.value })} data-testid="qa-task-type">
            <option>Call</option><option>Text</option><option>Email</option><option>Appointment</option><option>Follow-up</option><option>Paperwork</option><option>Other</option>
          </select>
        </Field>
        <Field label="Priority">
          <select className={inputCls} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="qa-task-priority">
            <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
          </select>
        </Field>
        <Field label="Due date"><input type="date" className={inputCls} value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="qa-task-date" /></Field>
        <Field label="Due time"><input type="time" className={inputCls} value={f.due_time} onChange={(e) => setF({ ...f, due_time: e.target.value })} data-testid="qa-task-time" /></Field>
      </div>
      <Field label="Notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="qa-task-notes" /></Field>
      <div className="flex items-center justify-end gap-2 pt-2">
        <CancelBtn onClick={onClose} />
        <SaveBtn saving={saving} label="Create Task" testid="qa-task-save" />
      </div>
    </form>
  );
}

/* ---------- New Message (saved as note + sms deep link) ---------- */
function NewMessageForm({ onClose }) {
  const [customer, setCustomer] = useState(null);
  const [f, setF] = useState({ channel: "Text", subject: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!customer?.id) { setError("Pick a customer."); return; }
    if (!f.body.trim()) { setError("Message body is required."); return; }
    setSaving(true);
    try {
      const prefix = `[${f.channel}${f.subject ? ` · ${f.subject}` : ""}] `;
      await api.post(`/admin/crm/notes`, { customer_id: customer.id, text: prefix + f.body });
      // Build SMS deep-link if Text
      if (f.channel === "Text" && customer.phone) {
        const phone = (customer.phone || "").replace(/[^\d+]/g, "");
        const url = `sms:${phone}?&body=${encodeURIComponent(f.body)}`;
        setSent({ kind: "sms", url });
        return;
      }
      if (f.channel === "Email" && customer.email) {
        const url = `mailto:${customer.email}?subject=${encodeURIComponent(f.subject || "Quick note")}&body=${encodeURIComponent(f.body)}`;
        setSent({ kind: "email", url });
        return;
      }
      setSent({ kind: "logged" });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to log message.");
    } finally { setSaving(false); }
  };

  if (sent) {
    return (
      <div data-testid="qa-message-sent">
        <Banner type="success">Message logged on the customer timeline.</Banner>
        {sent.url && <a href={sent.url} className="block bg-blue-600 hover:bg-blue-500 text-white text-center text-sm font-bold px-4 py-2 rounded mb-2" data-testid="qa-message-open-app">Open in {sent.kind === "sms" ? "Messages" : "Email"} →</a>}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="bg-white/10 hover:bg-white/20 text-white text-sm px-3 py-2 rounded" data-testid="qa-message-close">Done</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="qa-message-form">
      <Banner type="error" testid="qa-message-error">{error}</Banner>
      <Field label="Customer" required><CustomerPicker value={customer} onChange={setCustomer} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Channel">
          <select className={inputCls} value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid="qa-message-channel">
            <option>Text</option><option>Email</option><option>Internal Note</option>
          </select>
        </Field>
        {f.channel === "Email" && <Field label="Subject"><input className={inputCls} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="qa-message-subject" /></Field>}
      </div>
      <Field label="Message" required hint={f.channel === "Text" ? "Drafts a sms: deep-link — you finish sending from your phone's Messages app." : f.channel === "Email" ? "Drafts a mailto: link — opens in your default email client." : "Internal note only — not sent to the customer."}>
        <textarea className={inputCls} rows={5} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="qa-message-body" required />
      </Field>
      <div className="flex items-center justify-end gap-2 pt-2">
        <CancelBtn onClick={onClose} />
        <SaveBtn saving={saving} label="Log + Open" testid="qa-message-save" />
      </div>
    </form>
  );
}

/* ---------- New Appointment ---------- */
function NewAppointmentForm({ onClose }) {
  const [customer, setCustomer] = useState(null);
  const [f, setF] = useState({ title: "Test Drive", appt_type: "Test Drive", date: "", start_time: "", end_time: "", location: "Byers Chevrolet — Grove City", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!customer?.id) { setError("Pick a customer."); return; }
    if (!f.date || !f.start_time) { setError("Date and start time are required."); return; }
    setSaving(true);
    try {
      const starts_at = `${f.date}T${f.start_time}:00`;
      let duration_min = 30;
      if (f.end_time) {
        const [sh, sm] = f.start_time.split(":").map(Number);
        const [eh, em] = f.end_time.split(":").map(Number);
        duration_min = Math.max(15, (eh * 60 + em) - (sh * 60 + sm));
      }
      await api.post(`/admin/crm/appointments`, {
        customer_id: customer.id, appt_type: f.appt_type, starts_at,
        duration_min, location: f.location || null,
        notes: f.title !== f.appt_type ? `${f.title}${f.notes ? " — " + f.notes : ""}` : (f.notes || null),
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to create appointment.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="qa-appt-form">
      <Banner type="error" testid="qa-appt-error">{error}</Banner>
      <Field label="Customer" required><CustomerPicker value={customer} onChange={setCustomer} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Title"><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="qa-appt-title" /></Field>
        <Field label="Type">
          <select className={inputCls} value={f.appt_type} onChange={(e) => setF({ ...f, appt_type: e.target.value, title: e.target.value })} data-testid="qa-appt-type">
            <option>Test Drive</option><option>Delivery</option><option>Trade Appraisal</option><option>Paperwork</option><option>Follow-up</option><option>Service Intro</option><option>Other</option>
          </select>
        </Field>
        <Field label="Date" required><input type="date" className={inputCls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="qa-appt-date" required /></Field>
        <Field label="Start time" required><input type="time" className={inputCls} value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} data-testid="qa-appt-start" required /></Field>
        <Field label="End time"><input type="time" className={inputCls} value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} data-testid="qa-appt-end" /></Field>
        <Field label="Location"><input className={inputCls} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="qa-appt-location" /></Field>
      </div>
      <Field label="Notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="qa-appt-notes" /></Field>
      <div className="flex items-center justify-end gap-2 pt-2">
        <CancelBtn onClick={onClose} />
        <SaveBtn saving={saving} label="Schedule" testid="qa-appt-save" />
      </div>
    </form>
  );
}

/* ---------- New Note ---------- */
function NewNoteForm({ onClose }) {
  const [customer, setCustomer] = useState(null);
  const [f, setF] = useState({ title: "", body: "", visibility: "Internal" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!customer?.id) { setError("Pick a customer to attach this note to."); return; }
    if (!f.body.trim()) { setError("Note body is required."); return; }
    setSaving(true);
    try {
      const text = (f.title ? `${f.title}\n` : "") + f.body + (f.visibility === "Customer-facing" ? "\n\n[Visibility: Customer-facing]" : "");
      await api.post(`/admin/crm/notes`, { customer_id: customer.id, text });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to save note.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="qa-note-form">
      <Banner type="error" testid="qa-note-error">{error}</Banner>
      <Field label="Customer" required><CustomerPicker value={customer} onChange={setCustomer} /></Field>
      <Field label="Note title (optional)"><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="qa-note-title" /></Field>
      <Field label="Note" required><textarea className={inputCls} rows={5} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="qa-note-body" required /></Field>
      <Field label="Visibility">
        <select className={inputCls} value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })} data-testid="qa-note-vis">
          <option>Internal</option><option>Customer-facing</option>
        </select>
      </Field>
      <div className="flex items-center justify-end gap-2 pt-2">
        <CancelBtn onClick={onClose} />
        <SaveBtn saving={saving} label="Save Note" testid="qa-note-save" />
      </div>
    </form>
  );
}

/* ---------- Dispatcher ---------- */
export const QUICK_ACTION_KEYS = [
  { key: "customer", label: "+ New Customer", testid: "shell-qa-new-customer" },
  { key: "deal", label: "+ New Deal", testid: "shell-qa-new-deal" },
  { key: "trade", label: "+ New Trade Appraisal", testid: "shell-qa-new-trade" },
  { key: "task", label: "+ New Task", testid: "shell-qa-new-task" },
  { key: "message", label: "+ New Message", testid: "shell-qa-new-message" },
  { key: "appt", label: "+ New Appointment", testid: "shell-qa-new-appt" },
  { key: "note", label: "+ New Note", testid: "shell-qa-new-note" },
];

const TITLES = {
  customer: { t: "New Customer", h: "Capture a fresh lead or walk-in — checks for duplicates by phone & email." },
  deal: { t: "New Deal", h: "Attach a deal to an existing customer. Opens their Deal Jacket next." },
  trade: { t: "New Trade Appraisal", h: "Log a trade-in on an existing customer or create a new one in the same step." },
  task: { t: "New Task", h: "Add a follow-up task tied to a customer." },
  message: { t: "New Message", h: "Log a text/email/internal note. SMS/email drafts open in your device's native app." },
  appt: { t: "New Appointment", h: "Schedule a test drive, delivery, paperwork session, or follow-up." },
  note: { t: "New Note", h: "Drop a quick note onto a customer's timeline." },
};

export default function QuickActionsModals({ openKey, onClose }) {
  const FORM = useMemo(() => ({
    customer: NewCustomerForm,
    deal: NewDealForm,
    trade: NewTradeForm,
    task: NewTaskForm,
    message: NewMessageForm,
    appt: NewAppointmentForm,
    note: NewNoteForm,
  }), []);
  const FormCmp = openKey ? FORM[openKey] : null;
  const meta = openKey ? TITLES[openKey] : null;
  return (
    <Modal open={!!openKey} onClose={onClose} title={meta?.t || ""} helper={meta?.h} testid={openKey ? `qa-modal-${openKey}` : undefined} wide={openKey === "trade"}>
      {FormCmp ? <FormCmp onClose={onClose} /> : null}
    </Modal>
  );
}
